// The folder the cylinder pours into.
//
// Drawn as inline SVG rather than shipped as an image so the whole experiment
// stays inside this folder with no asset to lose track of, and so it can scale
// and be tinted from the sequence without a second file.
//
// Shaped after the desktop folder Robert sent: a back panel with photos peeking
// out of the top, and a front flap that they sit behind. The peeking photos are
// what make it read as "your files are IN here" at a glance rather than as a
// generic folder glyph — which matters, because this is the last frame of the
// animation and the only part a user is left looking at.

export default function ZipFolder({
  reveal = 0, active = 0, count = 0, total = 0, receiving = false, progress = null,
}) {
  // Present from the first moment and small, then lifting as it starts taking
  // files. The folder is the destination the whole time, so it earns its size by
  // becoming active rather than by appearing.
  const scale = (0.68 + 0.18 * reveal + 0.16 * active).toFixed(3);

  return (
    <div
      className="ph-folder"
      style={{
        opacity: reveal,
        transform: `translate(-50%, -50%) scale(${scale})`,
        "--ph-folder-glow": active.toFixed(3),
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 160 132" width="160" height="132">
        <defs>
          <linearGradient id="ph-f-back" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e8c877" />
            <stop offset="100%" stopColor="#d9b45c" />
          </linearGradient>
          <linearGradient id="ph-f-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f5da96" />
            <stop offset="100%" stopColor="#e2ba63" />
          </linearGradient>
        </defs>

        {/* Back panel with the tab */}
        <path
          d="M8 26a8 8 0 0 1 8-8h40l12 12h76a8 8 0 0 1 8 8v78a8 8 0 0 1-8 8H16a8 8 0 0 1-8-8Z"
          fill="url(#ph-f-back)"
        />

        {/* Photos peeking out — three, fanned, so the stack reads as contents
            rather than decoration. */}
        <g>
          <rect x="34" y="34" width="42" height="32" rx="3" fill="#cfd6dc" transform="rotate(-8 55 50)" />
          <rect x="60" y="30" width="42" height="32" rx="3" fill="#eef2f5" transform="rotate(3 81 46)" />
          <rect x="88" y="35" width="42" height="32" rx="3" fill="#dfe5ea" transform="rotate(11 109 51)" />
        </g>

        {/* Front flap, drawn last so the photos sit behind it */}
        <path
          d="M8 54h144v56a8 8 0 0 1-8 8H16a8 8 0 0 1-8-8Z"
          fill="url(#ph-f-front)"
        />
      </svg>

      {/* Counts UP from zero as files land, rather than announcing the total from
          the start (Robert 2026-08-01). It has to be a count of FILES, not of
          cards — see fileKeyOf in PhotoHelix — or 13 files would finish on 14.
          
          While the archive is still coming down the wire the count CANNOT move
          (nothing has landed, by construction), so showing a stationary "0 / 571"
          on a slow line would look like a hang. The transfer takes the line
          instead, and the count returns the moment there is something to count.

          `receiving` and `progress` are two questions, not one. Whether we are
          still waiting is known for certain; how far along is not, because a
          response without a length gives no fraction to show. Folding them into
          one value made a length-less transfer look finished and put the frozen
          zero back on screen — which is the exact thing this is here to avoid. */}
      <div className="ph-folder-count">
        {receiving ? (
          <span className="ph-folder-word">
            Receiving{progress ? ` ${Math.round(progress * 100)}%` : ""}…
          </span>
        ) : (
          <>
            <strong>{count}</strong>
            {total ? <span className="ph-folder-total"> / {total}</span> : null}
            <span className="ph-folder-word"> file{total === 1 ? "" : "s"}</span>
          </>
        )}
      </div>
    </div>
  );
}
